import {AstView} from 'rendition-core/types';
import React, {useState, useContext, createContext, ReactNode, useCallback} from 'react';
import {useProductContext} from 'Contexts/ProductContext';
export type HighlightType =
    | 'Hover'
    | 'Outline'
    | 'Selected'
    | 'Regroup'
    | 'Instance'
    | 'Width'
    | 'Padding'
    | 'MarginHorizontal'
    | 'MarginVertical'
    | 'PaddingHorizontal'
    | 'PaddingVertical'
    | 'Margin'
    | 'Absolute'
    | 'Assigned'
    | 'GroupingHighlight';

export type ColorInfo = {
    color: string;
    dashed?: boolean;
    showMargin?: boolean;
    showPadding?: boolean;
    width?: boolean;
    outline?: boolean;
    gap?: boolean;
    blur?: boolean;
    selectedBlur?: boolean;
    isHovered?: boolean;
    opacity?: number;
    stackable?: boolean;
    horizontalOnly?: boolean;
    verticalOnly?: boolean;
};

const HIGHLIGHT_COLOR_MAP: {[key in HighlightType]: ColorInfo} = {
    Selected: {color: `#e25000`, width: true, showMargin: true, showPadding: true, stackable: true},
    Instance: {color: `#8A84E2`},
    Width: {color: `Orange`, width: true, outline: true, stackable: true},
    Outline: {color: `purple`, dashed: true},
    Padding: {color: `blue`, gap: true, outline: true, showPadding: true, stackable: true},
    Margin: {color: `green`, showMargin: true, stackable: true},
    PaddingHorizontal: {
        color: `transparent`,
        gap: true,
        outline: false,
        showPadding: true,
        stackable: true,
        horizontalOnly: true,
    },
    MarginHorizontal: {color: `transparent`, showMargin: true, outline: false, stackable: true, horizontalOnly: true},
    PaddingVertical: {
        color: `blue`,
        gap: true,
        outline: true,
        showPadding: true,
        stackable: true,
        verticalOnly: true,
    },
    MarginVertical: {color: `green`, showMargin: true, stackable: true, verticalOnly: true},
    Regroup: {color: `Red`, showMargin: false, showPadding: false},
    Hover: {showMargin: false, color: `#79BEEE`, isHovered: true},
    Absolute: {color: `#FFE350`},
    Assigned: {color: `rgb(20, 255, 0)`},
    GroupingHighlight: {
        color: `Red`,
        opacity: 0.3,
        dashed: true,
    },
};

export interface NodeLocation {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface HighlighterState {
    highLightedNodes: {[id: string]: HighlightType[]};
    highLightedNodeColors: {[id: string]: ColorInfo};
    addNodeHighlight: (internalId: string, highlightType: HighlightType) => void;
    addNodeHighlightsClearOthers: (ids: string[], highlightType: HighlightType) => void;
    removeNodeHighlight: (internalId: string, highlightType: HighlightType) => void;
    clearHighlightByType: (highlightType: HighlightType | 'ALL') => void;
    recalculateHighlights: () => void;
    view: AstView;
    showGroupHighlight: boolean;
    setShowGroupHighlight: React.Dispatch<React.SetStateAction<boolean>>;
}

const HighlighterContext = createContext<HighlighterState>({
    highLightedNodes: {},
    highLightedNodeColors: {},
    addNodeHighlight: () => {},
    removeNodeHighlight: () => {},
    addNodeHighlightsClearOthers: () => {},
    clearHighlightByType: () => {},
    recalculateHighlights: () => {},
    view: {} as AstView,
    showGroupHighlight: true,
    setShowGroupHighlight: () => {},
});

export const useHighlighterContext = () => {
    return useContext(HighlighterContext);
};

const HighlighterContextProvider: React.FC<React.PropsWithChildren<{children: ReactNode; view: AstView}>> = ({
    children,
    view,
}) => {
    const {mode} = useProductContext();
    const [showGroupHighlight, setShowGroupHighlight] = useState(mode === 'Rendition_Code');

    const [highLightedNodes, setHighLightedNodes] = useState<{[id: string]: HighlightType[]}>({});

    const calcHighLightedNodeColors = () => {
        const newHighLightedNodeColors: {[id: string]: ColorInfo} = {};
        Object.keys(highLightedNodes).forEach((nodeId) => {
            const stackableColors = Object.keys(HIGHLIGHT_COLOR_MAP).filter((key) => {
                const color: ColorInfo = HIGHLIGHT_COLOR_MAP[key as HighlightType];
                const hasHighlight = highLightedNodes[nodeId].includes(key as HighlightType);
                return color.stackable && hasHighlight;
            });

            const color = Object.keys(HIGHLIGHT_COLOR_MAP).find((key) => {
                return highLightedNodes[nodeId].includes(key as HighlightType);
            });

            if (stackableColors.length > 1) {
                const stackableResult = stackableColors.reduce<ColorInfo>((acc, curr) => {
                    const colorInfo = HIGHLIGHT_COLOR_MAP[curr as HighlightType];
                    const keys = Object.keys(colorInfo);
                    keys.forEach((key) => {
                        if (colorInfo[key]) {
                            acc[key] = colorInfo[key];
                        }
                    });
                    return acc;
                }, {} as ColorInfo);

                newHighLightedNodeColors[nodeId] = stackableResult;
            } else if (color) {
                newHighLightedNodeColors[nodeId] = HIGHLIGHT_COLOR_MAP[color as HighlightType];
            }
        });

        return newHighLightedNodeColors;
    };
    const highLightedNodeColors = calcHighLightedNodeColors();

    const addNodeHighlight = useCallback((internalId: string, highlightType: HighlightType) => {
        setHighLightedNodes((existingNodes) => {
            if (!existingNodes[internalId]) {
                return {...existingNodes, [internalId]: [highlightType]};
            } else if (!existingNodes[internalId].includes(highlightType)) {
                existingNodes[internalId].push(highlightType);
                return {...existingNodes};
            }
            return existingNodes;
        });
    }, []);

    const removeNodeHighlight = useCallback((internalId: string, highlightType: HighlightType) => {
        setHighLightedNodes((oldHighLightedNodes) => {
            if (oldHighLightedNodes[internalId] && oldHighLightedNodes[internalId].includes(highlightType)) {
                oldHighLightedNodes[internalId] = oldHighLightedNodes[internalId].filter(
                    (type) => type !== highlightType
                );
                return {...oldHighLightedNodes};
            }
            return oldHighLightedNodes;
        });
    }, []);

    const clearHighlightByType = useCallback((highlightType: HighlightType | 'ALL') => {
        setHighLightedNodes((oldHighLightedNodes) => {
            let edit = false;
            Object.keys(oldHighLightedNodes).forEach((key) => {
                if (highlightType === 'ALL') {
                    oldHighLightedNodes[key] = [];
                    edit = true;
                } else if (oldHighLightedNodes[key] && oldHighLightedNodes[key].includes(highlightType)) {
                    oldHighLightedNodes[key] = oldHighLightedNodes[key].filter((type) => type !== highlightType);
                    edit = true;
                }
            });
            return edit ? {...oldHighLightedNodes} : oldHighLightedNodes;
        });
    }, []);

    const recalculateHighlights = useCallback(() => {
        setHighLightedNodes((value) => ({...value}));
    }, []);

    const addNodeHighlightsClearOthers = useCallback((ids: string[], highlightType: HighlightType) => {
        setHighLightedNodes((oldHighLightedNodes) => {
            let edit = false;

            // look at old if highlight remove it.
            Object.keys(oldHighLightedNodes).forEach((key) => {
                if (
                    oldHighLightedNodes[key] &&
                    oldHighLightedNodes[key].includes(highlightType) &&
                    !ids.includes(key)
                ) {
                    oldHighLightedNodes[key] = oldHighLightedNodes[key].filter((type) => type !== highlightType);
                    edit = true;
                }
            });

            // look at ids and add highlight if not found.
            ids.forEach((id) => {
                if (!oldHighLightedNodes[id]) {
                    oldHighLightedNodes[id] = [highlightType];
                    edit = true;
                } else if (!oldHighLightedNodes[id].includes(highlightType)) {
                    oldHighLightedNodes[id].push(highlightType);
                    edit = true;
                }
            });

            return edit ? {...oldHighLightedNodes} : oldHighLightedNodes;
        });
    }, []);

    return (
        <HighlighterContext.Provider
            value={{
                showGroupHighlight,
                setShowGroupHighlight,
                view,
                highLightedNodes,
                highLightedNodeColors,
                removeNodeHighlight,
                addNodeHighlight,
                clearHighlightByType,
                recalculateHighlights,
                addNodeHighlightsClearOthers,
            }}
        >
            {children}
        </HighlighterContext.Provider>
    );
};

export default HighlighterContextProvider;
